import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatCurrency, formatDate, formatDateLong } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { FileSpreadsheet, Plus, Search, Pencil, Trash2, ArrowLeft, ChevronLeft, ChevronRight, Send, Download, Loader2, Check, X, Upload, ExternalLink, MailOpen, Copy, Package, Clock, AlertTriangle, FileText, Mail, ScanLine } from "lucide-react";
import { toast } from "sonner";

const statusColors = {
  Draft: "bg-gray-100 text-gray-700 border-gray-300",
  Sent: "bg-blue-50 text-blue-700 border-blue-200",
  "Quote Received": "bg-green-50 text-green-700 border-green-200",
  Expired: "bg-amber-50 text-amber-700 border-amber-200",
  Cancelled: "bg-red-50 text-red-500 border-red-200 line-through",
};

export default function QuoteRequestsPage() {
  const { id } = useParams();
  if (id) return <QRDetail qrId={id} />;
  return <QRList />;
}

// ==================== QR List ====================
function QRList() {
  const [qrs, setQRs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  const fetchQRs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set("search", search);
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      const res = await api.get(`/quote-requests?${params}`);
      setQRs(res.data.quote_requests); setTotalPages(res.data.total_pages); setStats(res.data.stats);
    } catch { toast.error("Failed to load"); }
    setLoading(false);
  }, [search, statusFilter, page]);
  useEffect(() => { fetchQRs(); }, [fetchQRs]);

  const handleDelete = async () => {
    try { await api.delete(`/quote-requests/${deleteId}`); toast.success("Deleted"); setDeleteId(null); fetchQRs(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="quote-requests-page" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1F2937]">Quote Requests</h1>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <Badge className="bg-gray-100 text-gray-600 rounded-full text-xs">{stats.draft || 0} Draft</Badge>
            <Badge className="bg-blue-50 text-blue-700 rounded-full text-xs">{stats.sent || 0} Sent</Badge>
            <Badge className="bg-green-50 text-green-700 rounded-full text-xs">{stats.quote_received || 0} Received</Badge>
          </div>
        </div>
        <Button data-testid="create-qr-btn" onClick={() => setCreateOpen(true)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
          <Plus className="w-4 h-4 mr-1" /> New Quote Request
        </Button>
      </div>
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Search by QR # or supplier..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 rounded-sm border-gray-300" /></div>
        <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
          <SelectTrigger className="w-[180px] rounded-sm"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem>
            {["Draft", "Sent", "Quote Received", "Expired", "Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Card className="border border-gray-200 rounded-sm shadow-sm"><div className="overflow-x-auto">
        <table className="w-full text-sm"><thead><tr className="border-b bg-gray-50/50">
          <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">QR #</th>
          <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Supplier</th>
          <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Date</th>
          <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Due Date</th>
          <th className="p-3 text-center text-xs font-semibold uppercase text-gray-500">Products</th>
          <th className="p-3 text-left text-xs font-semibold uppercase text-gray-500">Status</th>
          <th className="p-3 text-right text-xs font-semibold uppercase text-gray-500">Actions</th>
        </tr></thead><tbody>
          {loading ? <tr><td colSpan={7} className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></td></tr>
          : qrs.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-gray-500">No quote requests found</td></tr>
          : qrs.map((qr) => {
            const isOverdue = qr.status === "Sent" && qr.due_date && qr.due_date < new Date().toISOString().split("T")[0];
            return (
              <tr key={qr.qr_id} className={`border-b border-gray-100 hover:bg-gray-50/50 ${isOverdue ? "bg-red-50/30" : ""}`}>
                <td className="p-3 cursor-pointer" onClick={() => navigate(`/quote-requests/${qr.qr_id}`)}>
                  <span className="font-mono font-semibold text-[#E63946] hover:underline text-sm">{qr.qr_number}</span></td>
                <td className="p-3 text-gray-700 max-w-[180px] break-words">{qr.supplier_name}</td>
                <td className="p-3 text-gray-500 text-xs">{formatDate(qr.request_date)}</td>
                <td className="p-3 text-xs"><span className={isOverdue ? "text-red-600 font-semibold" : "text-gray-500"}>{qr.due_date ? formatDate(qr.due_date) : "—"} {isOverdue && "!"}</span></td>
                <td className="p-3 text-center">{qr.products_count}</td>
                <td className="p-3"><Badge className={`${statusColors[qr.status] || ""} text-xs rounded-full border px-2 py-0.5`}>{qr.status}</Badge></td>
                <td className="p-3 text-right"><div className="flex items-center justify-end gap-1">
                  {["Draft"].includes(qr.status) && <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-[#E63946]" onClick={() => setDeleteId(qr.qr_id)}><Trash2 className="w-3.5 h-3.5" /></Button>}
                </div></td>
              </tr>);
          })}
        </tbody></table></div></Card>
      {totalPages > 1 && <div className="flex items-center justify-center gap-3">
        <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="rounded-sm"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
        <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="rounded-sm"><ChevronRight className="w-4 h-4" /></Button>
      </div>}
      {createOpen && <CreateQRDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(qr) => { setCreateOpen(false); navigate(`/quote-requests/${qr.qr_id}`); }} />}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}><AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Delete Quote Request?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent></AlertDialog>
    </div>
  );
}

// ==================== Create QR Dialog ====================
function CreateQRDialog({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [form, setForm] = useState({ supplier_id: "", due_date: "", priority: "Normal", your_reference: "", special_instructions: "", delivery_requirements: "", payment_terms_required: "", validity_period_required: "", internal_notes: "", include_current_cost: false });
  const [quantities, setQuantities] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/suppliers?limit=200").then((r) => setSuppliers(r.data.suppliers));
    api.get("/products?limit=500").then((r) => setProducts(r.data.products));
  }, []);

  const filteredProducts = products.filter((p) =>
    !productSearch || p.name?.toLowerCase().includes(productSearch.toLowerCase()) || p.sku?.toLowerCase().includes(productSearch.toLowerCase())
  );
  const toggleProduct = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
    if (!quantities[id]) setQuantities({ ...quantities, [id]: 1 });
  };
  const selectAll = () => {
    if (selectedIds.size === filteredProducts.length) { setSelectedIds(new Set()); return; }
    const s = new Set(filteredProducts.map((p) => p.product_id));
    const q = { ...quantities };
    filteredProducts.forEach((p) => { if (!q[p.product_id]) q[p.product_id] = 1; });
    setSelectedIds(s); setQuantities(q);
  };

  const handleCreate = async () => {
    if (!form.supplier_id) { toast.error("Select a supplier"); return; }
    if (selectedIds.size === 0) { toast.error("Select at least one product"); return; }
    setSaving(true);
    const prods = Array.from(selectedIds).map((id) => {
      const p = products.find((x) => x.product_id === id);
      return { product_id: id, sku: p?.sku || "", name: p?.name || "", description: p?.description || "",
        dimensions: p?.dimensions || "", weight: p?.weight || "", qty_requested: quantities[id] || 1,
        unit_of_measure: p?.unit_of_measure || "each", current_cost: p?.cost_price || 0,
        image_url: p?.image_url || "" };
    });
    try {
      const res = await api.post("/quote-requests", { ...form, products: prods });
      toast.success(`${res.data.qr_number} created with ${prods.length} products`);
      onCreated(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-sm">
        <DialogHeader><DialogTitle className="font-heading text-lg">
          {step === 1 ? "Step 1: Select Products" : "Step 2: Quote Request Details"}
        </DialogTitle></DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            {selectedIds.size > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-sm px-4 py-2 flex items-center justify-between sticky top-0 z-10">
                <span className="text-sm font-medium text-blue-700">{selectedIds.size} product{selectedIds.size > 1 ? "s" : ""} selected</span>
                <Button size="sm" onClick={() => setStep(2)} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm h-7 text-xs">Continue with {selectedIds.size} products</Button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input placeholder="Search products..." value={productSearch} onChange={(e) => setProductSearch(e.target.value)} className="pl-9 rounded-sm h-8 text-sm" /></div>
              <Button variant="outline" size="sm" onClick={selectAll} className="rounded-sm h-8 text-xs">{selectedIds.size === filteredProducts.length ? "Deselect All" : "Select All"}</Button>
            </div>
            <div className="max-h-[400px] overflow-y-auto border border-gray-200 rounded-sm">
              {filteredProducts.map((p) => {
                const sel = selectedIds.has(p.product_id);
                return (
                  <div key={p.product_id} className={`flex items-center gap-3 px-3 py-2 border-b border-gray-100 cursor-pointer transition-colors ${sel ? "bg-blue-50" : "hover:bg-gray-50"}`} onClick={() => toggleProduct(p.product_id)}>
                    <input type="checkbox" checked={sel} readOnly className="rounded border-gray-300 text-[#E63946]" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2"><span className="text-xs font-mono text-gray-500">{p.sku}</span><span className="text-sm font-medium truncate">{p.name}</span></div>
                      {p.description && <p className="text-xs text-gray-400 truncate">{p.description}</p>}
                    </div>
                    <span className="text-xs text-gray-500 shrink-0">{formatCurrency(p.cost_price || 0)}</span>
                    {sel && <Input type="number" min="1" value={quantities[p.product_id] || 1} onClick={(e) => e.stopPropagation()}
                      onChange={(e) => { e.stopPropagation(); setQuantities({ ...quantities, [p.product_id]: parseInt(e.target.value) || 1 }); }}
                      className="w-16 h-7 text-xs text-center rounded-sm" />}
                  </div>
                );
              })}
              {filteredProducts.length === 0 && <p className="text-sm text-gray-400 p-4 text-center">No products found</p>}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Supplier *</label>
                <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                  <SelectTrigger data-testid="qr-supplier-select" className="rounded-sm"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.company_name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Due Date</label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm" /></div>
              <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Priority</label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Low", "Normal", "High", "Urgent"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Your Reference</label>
                <Input value={form.your_reference} onChange={(e) => setForm({ ...form, your_reference: e.target.value })} placeholder="Optional" className="rounded-sm" /></div>
            </div>
            <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Special Instructions</label>
              <textarea value={form.special_instructions} onChange={(e) => setForm({ ...form, special_instructions: e.target.value })}
                placeholder="Special instructions for the supplier..." className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-16 resize-none focus:outline-none focus:ring-2 focus:ring-[#E63946]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Delivery Requirements</label>
                <Input value={form.delivery_requirements} onChange={(e) => setForm({ ...form, delivery_requirements: e.target.value })} className="rounded-sm" /></div>
              <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Payment Terms Required</label>
                <Input value={form.payment_terms_required} onChange={(e) => setForm({ ...form, payment_terms_required: e.target.value })} className="rounded-sm" /></div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.include_current_cost} onChange={(e) => setForm({ ...form, include_current_cost: e.target.checked })} className="rounded border-gray-300 text-[#E63946]" />
              <span className="text-sm text-gray-600">Include current cost prices in Excel</span></label>
            <div className="bg-gray-50 rounded-sm p-3"><p className="text-xs font-semibold text-gray-500 mb-1">{selectedIds.size} Products Selected:</p>
              <div className="flex flex-wrap gap-1">{Array.from(selectedIds).slice(0, 10).map((id) => {
                const p = products.find((x) => x.product_id === id);
                return <Badge key={id} className="bg-white text-xs rounded-sm">{p?.sku || "?"} x{quantities[id] || 1}</Badge>;
              })}{selectedIds.size > 10 && <Badge className="bg-gray-200 text-xs rounded-sm">+{selectedIds.size - 10} more</Badge>}</div>
            </div>
          </div>
        )}
        <DialogFooter className="mt-4">
          {step === 2 && <Button variant="outline" onClick={() => setStep(1)} className="rounded-sm">Back to Products</Button>}
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          {step === 1 ? (
            <Button onClick={() => { if (selectedIds.size === 0) { toast.error("Select at least one product"); return; } setStep(2); }} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              Continue ({selectedIds.size})
            </Button>
          ) : (
            <Button data-testid="save-qr-btn" onClick={handleCreate} disabled={saving} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Create Quote Request
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==================== QR Detail ====================
function QRDetail({ qrId }) {
  const [qr, setQR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendOpen, setSendOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ["admin", "sales_manager"].includes(user?.role);

  const fetchQR = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/quote-requests/${qrId}`); setQR(r.data); }
    catch { toast.error("Failed"); navigate("/quote-requests"); }
    setLoading(false);
  }, [qrId, navigate]);
  useEffect(() => { fetchQR(); }, [fetchQR]);

  const downloadExcel = async () => {
    setExcelLoading(true);
    try {
      const res = await api.get(`/quote-requests/${qrId}/excel`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `Quote_Request_${qr.qr_number}.xlsx`; a.click();
      toast.success("Excel downloaded"); fetchQR();
    } catch { toast.error("Excel generation failed"); }
    setExcelLoading(false);
  };

  const handleSend = async () => {
    try { const r = await api.post(`/quote-requests/${qrId}/send`); setQR(r.data); toast.success("Marked as sent"); setSendOpen(false); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleCancel = async () => {
    try { const r = await api.post(`/quote-requests/${qrId}/cancel`, { reason: cancelReason }); setQR(r.data); toast.success("Cancelled"); setCancelOpen(false); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async () => {
    try { await api.delete(`/quote-requests/${qrId}`); toast.success("Deleted"); navigate("/quote-requests"); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleCreatePO = async () => {
    try { const r = await api.post(`/quote-requests/${qrId}/create-po`); toast.success(`${r.data.po_number} created`); navigate(`/purchase-orders/${r.data.po_id}`); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading || !qr) return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-6 h-6 animate-spin text-[#E63946]" /></div>;

  const isOverdue = qr.status === "Sent" && qr.due_date && qr.due_date < new Date().toISOString().split("T")[0];
  const daysOverdue = isOverdue ? Math.floor((new Date() - new Date(qr.due_date)) / 86400000) : 0;

  return (
    <div data-testid="qr-detail-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate("/quote-requests")}><ArrowLeft className="w-4 h-4" /></Button>
          <div><h1 className="font-heading text-xl font-bold text-[#1F2937] flex items-center gap-2">{qr.qr_number}
            <Badge className={`${statusColors[qr.status] || ""} text-xs rounded-full border`}>{qr.status}</Badge>
            {qr.email_sent && <Badge className="rounded-full text-xs bg-green-50 text-green-700 border border-green-200"><Mail className="w-3 h-3 mr-1 inline" />Emailed</Badge>}
            {qr.priority === "High" && <Badge className="bg-amber-50 text-amber-700 text-[10px] rounded-full">High</Badge>}
            {qr.priority === "Urgent" && <Badge className="bg-red-50 text-red-600 text-[10px] rounded-full">Urgent</Badge>}
          </h1><p className="text-sm text-gray-500">{qr.supplier_name}</p></div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={downloadExcel} disabled={excelLoading} className="rounded-sm">
            {excelLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5 mr-1" />} {excelLoading ? "Generating..." : "Download Excel"}
          </Button>
          {qr.status === "Draft" && (
            <>
              <Button size="sm" onClick={() => setSendOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"><Send className="w-3.5 h-3.5 mr-1" /> Generate & Send</Button>
              <Button variant="outline" size="sm" onClick={() => setDeleteOpen(true)} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete</Button>
            </>
          )}
          {qr.status === "Sent" && (
            <>
              <Button size="sm" onClick={() => setUploadOpen(true)} className="bg-green-600 hover:bg-green-700 text-white rounded-sm"><Upload className="w-3.5 h-3.5 mr-1" /> Upload Supplier Quote</Button>
              {qr.email_sent && (
                <Button variant="outline" size="sm" onClick={async () => { try { await api.post("/email/resend", { type: "quote_request", id: qr.qr_id }); toast.success("Email resent to supplier"); } catch { toast.error("Failed to resend email"); }}} className="rounded-sm text-blue-600 border-blue-200 hover:bg-blue-50"><Mail className="w-3.5 h-3.5 mr-1" /> Resend Email</Button>
              )}
              <Button variant="outline" size="sm" onClick={() => setCancelOpen(true)} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50">Cancel QR</Button>
            </>
          )}
          {qr.status === "Quote Received" && isManager && (
            <Button data-testid="create-po-from-qr" size="sm" onClick={handleCreatePO} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm"><Package className="w-3.5 h-3.5 mr-1" /> Create Purchase Order</Button>
          )}
        </div>
      </div>

      {isOverdue && (
        <Card className="border-2 border-red-300 bg-red-50 rounded-sm"><CardContent className="p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" /><div><p className="text-sm font-semibold text-red-800">Response Overdue</p>
          <p className="text-xs text-red-700">Due {formatDateLong(qr.due_date)} — overdue by {daysOverdue} day{daysOverdue !== 1 ? "s" : ""}. Follow up with supplier.</p></div>
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div><p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Supplier</p><p className="text-sm font-medium cursor-pointer text-[#E63946] hover:underline" onClick={() => navigate(`/suppliers/${qr.supplier_id}`)}>{qr.supplier_name}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Request Date</p><p className="text-sm">{formatDateLong(qr.request_date)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Due Date</p><p className={`text-sm ${isOverdue ? "text-red-600 font-semibold" : ""}`}>{qr.due_date ? formatDateLong(qr.due_date) : "—"}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Priority</p><p className="text-sm">{qr.priority}</p></div>
            </div>
            <table className="w-full text-sm"><thead><tr className="border-b border-gray-200">
              <th className="pb-2 text-left text-xs text-gray-500">#</th><th className="pb-2 text-left text-xs text-gray-500">SKU</th>
              <th className="pb-2 text-left text-xs text-gray-500">Product</th><th className="pb-2 text-center text-xs text-gray-500">Qty</th>
              <th className="pb-2 text-left text-xs text-gray-500">Unit</th>
              {qr.include_current_cost && <th className="pb-2 text-right text-xs text-gray-500">Cost</th>}
              <th className="pb-2 text-left text-xs text-gray-500">Notes</th>
            </tr></thead><tbody>
              {(qr.products || []).map((p, i) => (
                <tr key={p.item_id} className="border-b border-gray-100">
                  <td className="py-2 text-gray-400 text-xs">{i + 1}</td>
                  <td className="py-2 text-xs font-mono text-gray-500">{p.sku || "—"}</td>
                  <td className="py-2"><p className="font-medium text-sm">{p.name}</p>{p.description && <p className="text-xs text-gray-400">{p.description}</p>}</td>
                  <td className="py-2 text-center font-medium">{p.qty_requested}</td>
                  <td className="py-2 text-xs text-gray-500">{p.unit_of_measure}</td>
                  {qr.include_current_cost && <td className="py-2 text-right text-xs">{formatCurrency(p.current_cost)}</td>}
                  <td className="py-2 text-xs text-gray-500">{p.notes || "—"}</td>
                </tr>
              ))}
            </tbody></table>
          </CardContent></Card>

          {(qr.special_instructions || qr.delivery_requirements || qr.payment_terms_required) && (
            <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-5 space-y-3">
              {qr.special_instructions && <div><p className="text-xs font-semibold uppercase text-gray-400 mb-1">Special Instructions</p><p className="text-sm text-gray-700">{qr.special_instructions}</p></div>}
              {qr.delivery_requirements && <div><p className="text-xs font-semibold uppercase text-gray-400 mb-1">Delivery Requirements</p><p className="text-sm text-gray-700">{qr.delivery_requirements}</p></div>}
              {qr.payment_terms_required && <div><p className="text-xs font-semibold uppercase text-gray-400 mb-1">Payment Terms</p><p className="text-sm text-gray-700">{qr.payment_terms_required}</p></div>}
            </CardContent></Card>
          )}

          {qr.supplier_quote_files?.length > 0 && (
            <Card className="border border-green-200 bg-green-50/30 rounded-sm shadow-sm"><CardContent className="p-5">
              <h3 className="text-sm font-semibold text-green-700 mb-3 flex items-center gap-2"><FileText className="w-4 h-4" /> Supplier Quote Response</h3>
              {qr.supplier_quote_ref && <p className="text-xs text-gray-500 mb-1">Ref: {qr.supplier_quote_ref}</p>}
              {qr.supplier_quote_total > 0 && <p className="text-sm font-bold text-green-700 mb-2">Quoted Total: {formatCurrency(qr.supplier_quote_total)}</p>}
              <div className="space-y-1">{qr.supplier_quote_files.map((f, i) => (
                <a key={i} href={f.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-xs text-blue-600 hover:underline bg-white rounded-sm px-2 py-1 border">
                  <FileText className="w-3 h-3" /> {f.filename}</a>
              ))}</div>
              {isManager && <Button data-testid="create-po-from-qr-bottom" size="sm" onClick={handleCreatePO} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm mt-3"><Package className="w-3.5 h-3.5 mr-1" /> Create PO from this Quote</Button>}
            </CardContent></Card>
          )}

          {qr.linked_po_ids?.length > 0 && (
            <Card className="border border-purple-200 bg-purple-50/20 rounded-sm shadow-sm"><CardContent className="p-5">
              <h3 className="text-sm font-semibold text-purple-700 mb-2">Linked Purchase Orders</h3>
              {qr.linked_po_ids.map((poId) => (
                <Button key={poId} variant="outline" size="sm" className="rounded-sm mr-2 text-xs" onClick={() => navigate(`/purchase-orders/${poId}`)}>
                  <ExternalLink className="w-3 h-3 mr-1" /> View PO
                </Button>
              ))}
            </CardContent></Card>
          )}
        </div>

        <div className="space-y-4">
          <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Status</span><Badge className={`${statusColors[qr.status] || ""} text-xs rounded-full border`}>{qr.status}</Badge></div>
              <div className="flex justify-between"><span className="text-gray-500">Products</span><span className="font-medium">{qr.products_count}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Created by</span><span>{qr.created_by_name}</span></div>
              {qr.sent_at && <div className="flex justify-between"><span className="text-gray-500">Sent</span><span className="text-xs">{formatDateLong(qr.sent_at)}</span></div>}
              {qr.quote_received_date && <div className="flex justify-between"><span className="text-gray-500">Received</span><span className="text-xs">{formatDateLong(qr.quote_received_date)}</span></div>}
            </div>
          </CardContent></Card>

          {qr.excel_file && (
            <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Excel Export</h3>
              <p className="text-xs text-gray-500 truncate mb-1">{qr.excel_file.filename}</p>
              <p className="text-[10px] text-gray-400 mb-2">Generated {formatDateLong(qr.excel_file.generated_at)}</p>
              <Button variant="outline" size="sm" className="w-full rounded-sm text-xs" onClick={downloadExcel}><Download className="w-3 h-3 mr-1" /> Download Again</Button>
            </CardContent></Card>
          )}

          <Card className="border border-gray-200 rounded-sm shadow-sm"><CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Audit Trail</h3>
            <AuditTrail entityType="quote_request" entityId={qrId} />
          </CardContent></Card>
        </div>
      </div>

      {/* Send Modal */}
      <Dialog open={sendOpen} onOpenChange={setSendOpen}><DialogContent className="max-w-lg rounded-sm">
        <DialogHeader><DialogTitle>Send Quote Request to Supplier</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="bg-gray-50 rounded-sm p-4 space-y-2">
            <p><span className="font-semibold">Supplier:</span> {qr.supplier_name}</p>
            <p><span className="font-semibold">Email:</span> {qr.supplier_email || "No email on file"}</p>
            <p><span className="font-semibold">Products:</span> {qr.products_count} items</p>
            {qr.due_date && <p><span className="font-semibold">Due Date:</span> {qr.due_date}</p>}
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-blue-800 text-xs">
            <Mail className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Clicking "Send Email & Mark Sent" will send a branded email with the Excel attachment directly to the supplier via Resend.
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadExcel} disabled={excelLoading} className="rounded-sm flex-1"><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> Download Excel</Button>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => {
              const text = `Dear ${qr.supplier_name},\n\nPlease find attached our Quote Request ${qr.qr_number} for ${qr.products_count} products.\n\nPlease complete the pricing column and return at your earliest convenience.${qr.due_date ? `\n\nResponse required by: ${qr.due_date}` : ""}\n\nKind regards`;
              navigator.clipboard.writeText(text); toast.success("Email text copied");
            }}><Copy className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setSendOpen(false)} className="rounded-sm">Cancel</Button>
          <Button data-testid="mark-qr-sent" onClick={handleSend} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"><Mail className="w-3.5 h-3.5 mr-1" /> Send Email & Mark Sent</Button>
        </DialogFooter>
      </DialogContent></Dialog>

      {/* Upload Response */}
      {uploadOpen && <UploadResponseDialog qr={qr} open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={(data) => { setQR(data); setUploadOpen(false); }} />}

      {/* Cancel */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}><AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Cancel Quote Request?</AlertDialogTitle><AlertDialogDescription>
          <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Reason (optional)..." className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm h-16 resize-none mt-2" />
        </AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Go Back</AlertDialogCancel>
          <AlertDialogAction onClick={handleCancel} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Cancel QR</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent></AlertDialog>

      {/* Delete */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}><AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Delete Quote Request?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleDelete} className="bg-[#E63946] hover:bg-[#C1121F] text-white rounded-sm">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent></AlertDialog>
    </div>
  );
}

function UploadResponseDialog({ qr, open, onClose, onUploaded }) {
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({ supplier_quote_ref: "", supplier_quote_total: "", supplier_quote_valid_until: "", date_received: new Date().toISOString().split("T")[0] });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);

  const handleUpload = async (e) => {
    if (!e.target.files?.length) return;
    setUploading(true);
    const fd = new FormData();
    for (let f of e.target.files) fd.append("files", f);
    try { const r = await api.post("/upload-multiple", fd, { headers: { "Content-Type": "multipart/form-data" } }); setFiles([...files, ...r.data.files]); }
    catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const handleScanInvoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setOcrResult(null);

    // Upload the file
    const fd = new FormData();
    fd.append("files", file);
    try { const r = await api.post("/upload-multiple", fd, { headers: { "Content-Type": "multipart/form-data" } }); setFiles((prev) => [...prev, ...r.data.files]); }
    catch { /* continue */ }

    // OCR extract
    const ocrFd = new FormData();
    ocrFd.append("file", file);
    try {
      const res = await api.post("/ocr/extract", ocrFd, { headers: { "Content-Type": "multipart/form-data" } });
      const d = res.data;
      if (d.success) {
        setOcrResult(d);
        setForm((prev) => ({
          ...prev,
          supplier_quote_ref: d.invoice_number || prev.supplier_quote_ref,
          supplier_quote_total: d.total_amount ? String(d.total_amount) : prev.supplier_quote_total,
          date_received: d.date || prev.date_received,
        }));
        toast.success(`Invoice scanned — ${d.confidence} confidence`);
      } else {
        toast.error(d.error || "OCR failed. Enter details manually.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "OCR not available. Enter details manually.");
    }
    setScanning(false);
  };

  const handleSave = async () => {
    if (!files.length) { toast.error("Upload at least one file"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/quote-requests/${qr.qr_id}/upload-response`, { ...form, files, supplier_quote_total: parseFloat(form.supplier_quote_total) || 0 });
      toast.success("Supplier quote saved"); onUploaded(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}><DialogContent className="max-w-lg rounded-sm">
      <DialogHeader><DialogTitle>Upload Supplier Quote — {qr.qr_number}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        {/* OCR Scan */}
        <div className="border-2 border-dashed border-blue-200 bg-blue-50/50 rounded-sm p-3">
          <label className="flex items-center justify-center gap-2 cursor-pointer text-sm font-medium text-blue-700 hover:text-blue-900">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanLine className="w-4 h-4" />}
            {scanning ? "Analyzing invoice..." : "Scan Supplier Invoice (auto-fill with AI)"}
            <input data-testid="ocr-scan-invoice" type="file" accept="image/*,.pdf" onChange={handleScanInvoice} className="hidden" disabled={scanning} />
          </label>
        </div>

        {ocrResult && (
          <div className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-sm border ${
            ocrResult.confidence === "high" ? "bg-green-50 text-green-700 border-green-200" :
            ocrResult.confidence === "medium" ? "bg-amber-50 text-amber-700 border-amber-200" :
            "bg-red-50 text-red-600 border-red-200"}`}>
            <ScanLine className="w-3.5 h-3.5" />
            OCR: {ocrResult.confidence_score}% — {ocrResult.confidence}
            {ocrResult.warnings?.length > 0 && <span className="ml-1 text-gray-500">({ocrResult.warnings.join(", ")})</span>}
          </div>
        )}

        <div className="border border-dashed border-gray-300 rounded-sm p-4">
          <label className="flex items-center justify-center gap-2 cursor-pointer text-sm text-gray-500 hover:text-[#E63946]">
            <Upload className="w-4 h-4" /> {uploading ? "Uploading..." : "Upload supplier's quote (PDF, Excel, Images)"}
            <input type="file" multiple accept=".pdf,.xlsx,.xls,.doc,.docx,.png,.jpg,.jpeg" onChange={handleUpload} className="hidden" />
          </label>
          {files.length > 0 && <div className="mt-2 space-y-1">{files.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded-sm px-2 py-1">
              <FileText className="w-3 h-3" /> {f.filename}
              <Button variant="ghost" size="icon" className="h-4 w-4 ml-auto" onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="w-3 h-3" /></Button>
            </div>))}</div>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Supplier Quote Ref</label>
            <Input value={form.supplier_quote_ref} onChange={(e) => setForm({ ...form, supplier_quote_ref: e.target.value })} placeholder="Supplier's ref #" className="rounded-sm" /></div>
          <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Quote Total (R)</label>
            <Input type="number" value={form.supplier_quote_total} onChange={(e) => setForm({ ...form, supplier_quote_total: e.target.value })} placeholder="0.00" className="rounded-sm" /></div>
          <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Date Received</label>
            <Input type="date" value={form.date_received} onChange={(e) => setForm({ ...form, date_received: e.target.value })} className="rounded-sm" /></div>
          <div><label className="text-xs font-semibold uppercase text-gray-500 mb-1 block">Valid Until</label>
            <Input type="date" value={form.supplier_quote_valid_until} onChange={(e) => setForm({ ...form, supplier_quote_valid_until: e.target.value })} className="rounded-sm" /></div>
        </div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
        <Button data-testid="save-response-btn" onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700 text-white rounded-sm">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />} Save & Mark as Received
        </Button>
      </DialogFooter>
    </DialogContent></Dialog>
  );
}

function AuditTrail({ entityType, entityId }) {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    api.get(`/audit-trail?entity_type=${entityType}&entity_id=${entityId}`).then((r) => setEntries(r.data.entries || [])).catch(() => {});
  }, [entityType, entityId]);
  if (!entries.length) return <p className="text-xs text-gray-400">No activity yet</p>;
  return <div className="space-y-2">{entries.slice(0, 10).map((e, i) => (
    <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#E63946] mt-1.5 shrink-0" />
      <div><p className="text-xs text-gray-700">{e.details}</p><p className="text-[10px] text-gray-400">{e.user_name} — {formatDateLong(e.created_at)}</p></div>
    </div>))}</div>;
}
